import React, { useCallback, useState } from "react";
import { useT } from "@transifex/react";
import { Modal } from "storybook/components/modal";
import {
  QuestionnaireGlobalStyle,
  LayoutContainer,
  Header,
  LogoWrapper,
  HeaderActions,
  RestartLink,
  ProgressBarContainer,
  ProgressBarTrack,
  ProgressBarFill,
  ProgressText,
  MainContent,
  Footer,
  StartOverModalTitle,
  StartOverModalText,
  StartOverModalActions,
  StartOverModalButtonSecondary,
  StartOverModalButtonPrimary,
} from "./styles";
import { Logo } from "../../components/logo";

interface Props {
  children: React.ReactNode;
  progress?: number;
  logoUrl?: string;
  logoMaxHeight?: string;
  showProgress?: boolean;
  showRestart?: boolean;
  onRestart?: () => void;
  languagePicker?: React.ReactNode;
}

const QuestionnaireLayout: React.FC<Props> = ({
  children,
  progress = 0,
  logoUrl,
  logoMaxHeight = "35px",
  showProgress = true,
  showRestart = false,
  onRestart,
  languagePicker,
}) => {
  const t = useT();
  const [startOverConfirmOpen, setStartOverConfirmOpen] = useState(false);

  const handleStartOverClick = useCallback(() => {
    setStartOverConfirmOpen(true);
  }, []);

  const handleStartOverConfirmClose = useCallback(() => {
    setStartOverConfirmOpen(false);
  }, []);

  const handleStartOverConfirm = useCallback(() => {
    setStartOverConfirmOpen(false);
    onRestart?.();
  }, [onRestart]);

  return (
    <LayoutContainer>
      <QuestionnaireGlobalStyle />
      <Header>
        <LogoWrapper>
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={t("Logo")}
              style={{ maxHeight: logoMaxHeight }}
            />
          ) : (
            <Logo />
          )}
        </LogoWrapper>
        <HeaderActions>
          {showRestart && onRestart && (
            <RestartLink type="button" onClick={handleStartOverClick}>
              {t("Start over")}
            </RestartLink>
          )}
          {languagePicker}
        </HeaderActions>
      </Header>
      <Modal
        theme="default"
        open={startOverConfirmOpen}
        onClose={handleStartOverConfirmClose}
        padding="24px"
      >
        <Modal.Head alignTitle="left">
          <StartOverModalTitle>{t("Start over?")}</StartOverModalTitle>
        </Modal.Head>
        <Modal.Body>
          <div style={{ wordBreak: "break-word" }}>
            <StartOverModalText>
              {t(
                "Your answers on this survey will be cleared and you will go back to the beginning."
              )}
            </StartOverModalText>
          </div>
        </Modal.Body>
        <Modal.Actions>
          <StartOverModalActions>
            <StartOverModalButtonSecondary
              type="button"
              onClick={handleStartOverConfirmClose}
            >
              {t("Cancel")}
            </StartOverModalButtonSecondary>
            <StartOverModalButtonPrimary
              type="button"
              onClick={handleStartOverConfirm}
            >
              {t("Start over")}
            </StartOverModalButtonPrimary>
          </StartOverModalActions>
        </Modal.Actions>
      </Modal>
      {showProgress && (
        <ProgressBarContainer>
          <ProgressBarTrack>
            <ProgressBarFill progress={progress} />
          </ProgressBarTrack>
          <ProgressText>
            {Math.round(progress)}% {t("complete")}
          </ProgressText>
        </ProgressBarContainer>
      )}
      <MainContent>{children}</MainContent>
      <Footer>
        &copy; {new Date().getFullYear()} CancerIQ. {t("All rights reserved.")}
      </Footer>
    </LayoutContainer>
  );
};

export default QuestionnaireLayout;
